import React, { useState, useEffect } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions, PERMISSIONS, PermissionGuard } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as usersApi from '../api/users';

export function AdminUsers() {
  const { selectedOrg } = useOrg();
  const { canReadUsers, canWriteUsers } = usePermissions();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedOrg || !canReadUsers) {
      setLoading(false);
      return;
    }

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [usersData, rolesData] = await Promise.all([
          usersApi.getOrgUsers(selectedOrg.id),
          usersApi.getOrgRoles(selectedOrg.id)
        ]);
        setUsers(usersData);
        setRoles(rolesData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [selectedOrg, canReadUsers]);

  const handleRoleChange = async (userId, newRole) => {
    setSaving(true);
    try {
      const result = await usersApi.updateUserRole(selectedOrg.id, userId, newRole);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: result.user.role, permissions: result.user.permissions } : u
      ));
      setEditingUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!canReadUsers) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view users.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader 
        title="Users"
        subtitle={`Manage user roles for ${selectedOrg?.name}`}
        breadcrumbs={[
          { label: 'Admin', href: '#' },
          { label: 'Users' }
        ]}
      />

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading users...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Permissions</th>
                {canWriteUsers && <th style={styles.th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={styles.tr}>
                  <td style={styles.td}>{user.name}</td>
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>
                    {editingUser === user.id ? (
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={saving}
                        style={styles.select}
                      >
                        {roles.map(role => (
                          <option key={role.name} value={role.name}>
                            {formatRoleName(role.name)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={styles.roleBadge}>{formatRoleName(user.role)}</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.permissions}>
                      {user.permissions?.slice(0, 3).map(perm => (
                        <span key={perm} style={styles.permBadge}>
                          {formatPermission(perm)}
                        </span>
                      ))}
                      {user.permissions?.length > 3 && (
                        <span style={styles.permMore}>+{user.permissions.length - 3}</span>
                      )}
                    </div>
                  </td>
                  {canWriteUsers && (
                    <td style={styles.td}>
                      {editingUser === user.id ? (
                        <button
                          onClick={() => setEditingUser(null)}
                          style={styles.cancelButton}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingUser(user.id)}
                          style={styles.editButton}
                        >
                          Edit Role
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.rolesSection}>
        <h3>Available Roles</h3>
        <div style={styles.rolesGrid}>
          {roles.map(role => (
            <div key={role.name} style={styles.roleCard}>
              <h4 style={styles.roleTitle}>{formatRoleName(role.name)}</h4>
              <div style={styles.rolePermissions}>
                {role.permissions.map(perm => (
                  <span key={perm} style={styles.permBadge}>{formatPermission(perm)}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatRoleName(role) {
  return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function formatPermission(perm) {
  return perm.replace(':', ' ');
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  roleBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    background: '#edf2f7',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  permissions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem'
  },
  permBadge: {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    background: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '0.625rem',
    color: '#4a5568'
  },
  permMore: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  select: {
    padding: '0.5rem',
    borderRadius: '4px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  editButton: {
    padding: '0.375rem 0.75rem',
    background: '#3182ce',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  cancelButton: {
    padding: '0.375rem 0.75rem',
    background: '#e2e8f0',
    color: '#4a5568',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  rolesSection: {
    marginTop: '3rem'
  },
  rolesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '1rem',
    marginTop: '1rem'
  },
  roleCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '1rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  roleTitle: {
    margin: '0 0 0.75rem 0',
    fontSize: '1rem'
  },
  rolePermissions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem'
  }
};
